import type { SidebarGroup } from "@plerity/dashboard-shell";
import { createShell, PORTAL_URLS, type SettingsTab } from "@plerity/dashboard-shell";
import { ConfirmProvider } from "@plerity/ui-core";
import {
  Activity,
  Archive,
  BarChart3,
  Bot,
  Calendar,
  CalendarClock,
  CheckCircle,
  Copy,
  Database,
  FileImage,
  FileText,
  Film,
  Handshake,
  Hash,
  History,
  Image,
  ImagePlay,
  Images,
  LayoutDashboard,
  LayoutGrid,
  Leaf,
  Lightbulb,
  Link2,
  Megaphone,
  MessageSquare,
  Mic,
  Paintbrush,
  Palette,
  PenSquare,
  PieChart,
  Radar,
  Repeat,
  Rocket,
  Scissors,
  Settings,
  ShieldCheck,
  Sparkles,
  Target,
  Upload,
  UserCheck,
  Users,
  UsersRound,
  Video,
  Webhook,
} from "lucide-react";
import { lazy, Suspense } from "react";
import { useContentLibraryPendingBadge } from "./api/contentLibrary";
import { useInboxUnreadCount } from "./api/queries";
import { ClientBrandSwitcher } from "./components/ClientBrandSwitcher";
import { CommandPalette } from "./components/command-palette/CommandPalette";
import LearnTipsProvider from "./components/learn-tips/LearnTipsProvider";
import { NotificationBell } from "./components/NotificationBell";
// Redirect shims keep the incoming query (e.g. /social/compose?design_id=…).
import { redirectPreservingQuery } from "./lib/redirectPreservingQuery";
import FacebookCallback from "./routes/auth/FacebookCallback";
import GoogleBusinessCallback from "./routes/auth/GoogleBusinessCallback";
import LinkedInCallback from "./routes/auth/LinkedInCallback";
import PinterestCallback from "./routes/auth/PinterestCallback";
import ThreadsCallback from "./routes/auth/ThreadsCallback";
import TikTokCallback from "./routes/auth/TikTokCallback";
import { SocialSettingsTabContent } from "./routes/settings/SettingsTabContent";

// Public client review page (no login) — lazy so it never weighs on the app shell.
const ClientReview = lazy(() => import("./routes/public/ClientReview"));
// Public client self-connect page (no login, lane D1) — token in the #t= fragment.
const ClientConnect = lazy(() => import("./routes/public/ClientConnect"));
// Public guest footage-upload page (no login, lane LIB) — token in the #t= fragment.
const FootageUpload = lazy(() => import("./routes/public/FootageUpload"));

const sidebarGroups: SidebarGroup[] = [
  {
    // Standalone top-level destinations — no section header. Tyler note 238
    // (2026-06-08, web-social): Design Studio / Overview / Analytics / Inbox
    // were each the lone visible item in their own one-item group; promoted
    // here to standalone nav entries via a title-less group (SidebarNav
    // renders no header when title === ""). Plan:
    // agents/plans/social_tester_notes_remediation_plan_2026-06-08.md
    title: "",
    defaultOpen: true,
    routes: [
      // Unified Design Studio hub — New Post / Design Studio (format picker) /
      // Templates are now `?tab=` panels inside this single page.
      // Consolidated 2026-06-08 — see routes/social/DesignStudio.tsx and
      // agents/plans/social_design_studio_consolidation_plan_2026-06-08.md.
      {
        path: "social/studio",
        label: "Design Studio",
        icon: Palette,
        loader: () => import("./routes/social/DesignStudio"),
      },
      // ── Redirect shims → Design Studio hub tabs. Preserve deep links,
      //    bookmarks, and internal <Link>/navigate() targets after consolidation
      //    (PostComposer "Create New Design" + DesignGallery "New Design" both
      //    navigate to /social/designs/new). ──
      {
        path: "social/compose",
        label: "New Post",
        icon: PenSquare,
        loader: redirectPreservingQuery("/social/studio?tab=compose"),
        hidden: true,
      },
      {
        path: "social/designs/new",
        label: "Design Studio",
        icon: Palette,
        loader: redirectPreservingQuery("/social/studio?tab=create"),
        hidden: true,
      },
      {
        path: "social/designs/templates",
        label: "Templates",
        icon: Copy,
        loader: redirectPreservingQuery("/social/studio?tab=templates"),
        hidden: true,
      },
      // "social/video/new" intentionally has NO separate route entry here —
      // the "social/video/:id" route below matches "new" as a literal `id`
      // (VideoEditor.tsx already treats `id === "new"` as "unsaved project"),
      // and routing both through the SAME lazy-loaded component reference
      // avoids a remount when the first save replaces "new" with a real id
      // in the URL (2026-10-04, social video autonomy E1 — two separate
      // `loader: () => import(...)` closures for the same module produce two
      // distinct React.lazy() component identities, so switching between
      // them unmounted the editor and wiped undo history/selection/playhead).
      // Single consolidated hub. Dashboard / Clients / Calendar / All Posts /
      // Drafts / Approvals / Accounts are now `?tab=` panels inside this page.
      // Consolidated 2026-06-07 — see routes/social/Management.tsx.
      {
        path: "social/overview",
        label: "Overview",
        icon: LayoutDashboard,
        loader: () => import("./routes/social/Management"),
      },
      // Content Autopilot — plan a period of on-brand drafts per client; output
      // lands in the approval queue (never auto-approved or scheduled).
      // agents/plans/social_20_agent_ux_studio_sweep_plan_2026-10-01.md (W9).
      {
        path: "social/autopilot",
        label: "Autopilot",
        icon: Rocket,
        loader: () => import("./routes/social/Autopilot"),
      },
      // B3 Posting schedule — weekly slots per account + "Add to queue" target (migration 056).
      {
        path: "social/queue",
        label: "Posting Schedule",
        icon: CalendarClock,
        loader: () => import("./routes/social/PostingSchedule"),
      },
      // Recurring series ("Burger Monday") — weekly features with a photo requested ahead of each
      // episode (migration 116, /api/series). Deep link: /social/series?series=<id>.
      {
        path: "social/series",
        label: "Recurring Series",
        icon: Repeat,
        loader: () => import("./routes/social/Series"),
      },
      // Post detail — real detail route, kept resolvable but out of the sidebar.
      {
        path: "social/posts/:id",
        label: "Post Detail",
        icon: FileText,
        loader: () => import("./routes/social/PostDetail"),
        hidden: true,
      },
      // ── Redirect shims → Overview hub tabs. Preserve deep links, bookmarks,
      //    and existing internal <Link>/navigate() targets after consolidation. ──
      {
        path: "social/clients",
        label: "Clients",
        icon: UsersRound,
        loader: redirectPreservingQuery("/social/overview?tab=clients"),
        hidden: true,
      },
      {
        path: "social/posts",
        label: "All Posts",
        icon: FileText,
        loader: redirectPreservingQuery("/social/overview?tab=posts"),
        hidden: true,
      },
      // Content Library — agent-generated reels/stills/designs/draft briefs
      // land here for a first-pass approve/reject BEFORE a post is eligible
      // for the Approvals queue or the calendar. Distinct stage from
      // Approvals above (routes/social/Approvals.tsx reviews posts already
      // slated for publish; this page reviews raw generated media).
      {
        path: "social/content-library",
        label: "Content Library",
        icon: Images,
        loader: () => import("./routes/social/ContentLibrary"),
        useBadge: () => useContentLibraryPendingBadge(),
      },
      {
        path: "social/approvals",
        label: "Approvals",
        icon: CheckCircle,
        loader: redirectPreservingQuery("/social/overview?tab=approvals"),
        hidden: true,
      },
      {
        path: "social/drafts",
        label: "Drafts",
        icon: FileText,
        loader: redirectPreservingQuery("/social/overview?tab=drafts"),
        hidden: true,
      },
      {
        path: "social/calendar",
        label: "Content Calendar",
        icon: Calendar,
        loader: redirectPreservingQuery("/social/overview?tab=calendar"),
        hidden: true,
      },
      // ── Analytics (was its own one-item "Analytics" group) ──
      {
        // Unified analytics shell — all sub-tabs driven by ?tab= query param.
        // Old paths below are kept as redirect-only routes (hidden) so existing
        // bookmarks keep working.
        path: "social/analytics",
        label: "Analytics",
        icon: BarChart3,
        loader: () => import("./routes/social/Analytics"),
      },
      {
        path: "social/analytics/platforms",
        label: "Platform Breakdown",
        icon: PieChart,
        loader: redirectPreservingQuery("/social/analytics?tab=platforms"),
        hidden: true,
      },
      {
        path: "social/analytics/designs",
        label: "Design Performance",
        icon: Image,
        loader: redirectPreservingQuery("/social/analytics?tab=designs"),
        hidden: true,
      },
      {
        path: "social/analytics/ai",
        label: "AI Performance",
        icon: Bot,
        loader: redirectPreservingQuery("/social/analytics?tab=ai"),
        hidden: true,
      },
      {
        path: "social/copywriter/analytics",
        label: "Copywriter Stats",
        icon: Sparkles,
        loader: redirectPreservingQuery("/social/analytics?tab=copywriter"),
        hidden: true,
      },
      // ── Inbox (was its own one-item "Engagement" group) ──
      {
        path: "social/inbox",
        label: "Inbox",
        icon: MessageSquare,
        loader: () => import("./routes/social/Inbox"),
        // Live needs-reply count (30 s poll, brand-scoped) — hidden at 0.
        useBadge: () => useInboxUnreadCount().data?.unread,
      },
      // C1 Social listening — mentions, tags/UGC, keyword alerts (/api/listening/*, migration 060).
      {
        path: "social/listening",
        label: "Listening",
        icon: Radar,
        loader: () => import("./routes/social/Listening"),
      },
      // Outbound engagement queue — the daily "engagement block" (one-tap
      // through suggested partner/member/mention/tag/competitor/local
      // targets: open, paste the suggested comment, done or skip).
      // Migration 088; policy lives in Brand Kit -> Engagement automation.
      {
        path: "social/engagement",
        label: "Engagement",
        icon: Handshake,
        loader: () => import("./routes/social/Engagement"),
      },
    ],
  },
  {
    title: "Library",
    routes: [
      // B3 Evergreen library — recyclable posts by content category (migration 056).
      {
        path: "social/evergreen",
        label: "Evergreen Library",
        icon: Leaf,
        loader: () => import("./routes/social/EvergreenLibrary"),
      },
      // B2 Link in Bio — branded bio page per client (migration 055).
      {
        path: "social/link-in-bio",
        label: "Link in Bio",
        icon: Link2,
        loader: () => import("./routes/social/LinkInBio"),
      },
      {
        path: "social/link-in-bio/:id",
        label: "Edit Link in Bio",
        icon: Link2,
        loader: () => import("./routes/social/LinkInBioEditor"),
        hidden: true,
      },
      {
        path: "social/designs",
        label: "My Designs",
        icon: LayoutGrid,
        loader: () => import("./routes/social/DesignGallery"),
      },
      {
        path: "social/designs/:id",
        label: "Edit Design",
        icon: Palette,
        loader: () => import("./routes/social/DesignEditor"),
        hidden: true,
        // Canva-style full-screen studio: no sidebar/header/FABs; the editor
        // renders its own "Designs" back button.
        hideChrome: true,
      },
      {
        path: "social/assets",
        label: "Assets",
        icon: FileImage,
        loader: () => import("./routes/social/AssetLibrary"),
      },
      {
        path: "social/brand-kit",
        label: "Brand Kit",
        icon: Paintbrush,
        loader: () => import("./routes/social/BrandKitEditor"),
      },
      {
        // Redirect shim — Brand Kit lived at /social/brand until 2026-07-16,
        // when the path was renamed to /social/brand-kit so the sidebar
        // prefix-match no longer highlights "Brand Kit" on /social/brand/voice.
        // Preserves old bookmarks + deep links.
        path: "social/brand",
        label: "Brand Kit (legacy)",
        icon: Paintbrush,
        loader: redirectPreservingQuery("/social/brand-kit"),
        hidden: true,
      },
      {
        path: "social/video",
        label: "Video Projects",
        icon: Film,
        loader: () => import("./routes/social/VideoGallery"),
      },
      {
        // Quick Reel (2026-10-03): one clip → in/out → 9:16 reel with engine
        // captions, rendered server-side. Moved here from Plerity Video.
        path: "social/video/quick-reel",
        label: "Quick Reel",
        icon: Scissors,
        loader: () => import("./routes/social/QuickReel"),
        // Explicit: keep the shell chrome — `social/video/:id` (hideChrome) would otherwise match "quick-reel".
        hideChrome: false,
      },
      {
        path: "social/video/:id",
        label: "Edit Video",
        icon: Film,
        loader: () => import("./routes/social/VideoEditor"),
        hidden: true,
        // Canva-style full-screen studio (matches the Design Editor convention
        // above): no sidebar/header/FABs; the editor renders its own back button.
        hideChrome: true,
      },
      // Footage Library (lane LIB, 2026-10-04) — brand-scoped raw footage,
      // consent manager, guest upload links (backend lane F1, migration 067).
      {
        path: "social/footage",
        label: "Footage",
        icon: Video,
        loader: () => import("./routes/social/FootageLibrary"),
      },
    ],
  },
  {
    title: "AI & Knowledge",
    routes: [
      // B6 Content Ideas — trending short-video ideas + storyboards (routers/content_ideas.py).
      {
        path: "social/ideas",
        label: "Content Ideas",
        icon: Lightbulb,
        loader: () => import("./routes/social/ContentIdeas"),
      },
      {
        path: "social/ideas/insights",
        label: "What's Working",
        icon: BarChart3,
        loader: () => import("./routes/social/ContentInsights"),
      },
      // SM-062 trend radar — trends to join, posting windows, the 4-point screen (routers/trend_signals.py).
      {
        path: "social/ideas/trends",
        label: "Trend Radar",
        icon: Radar,
        loader: () => import("./routes/social/TrendRadar"),
      },
      {
        path: "social/copywriter/chat",
        label: "AI Chat",
        icon: MessageSquare,
        loader: () => import("./routes/social/CopywriterChat"),
      },
      {
        path: "social/copywriter/hashtags",
        label: "Hashtag Research",
        icon: Hash,
        loader: () => import("./routes/social/HashtagResearch"),
      },
      {
        path: "social/brand/voice",
        label: "Brand Voice",
        icon: Mic,
        loader: () => import("./routes/social/BrandVoice"),
      },
      {
        // Knowledge Base + Knowledge Health are consolidated into one hub with
        // ?tab= panels (Tyler note 239, 2026-06-08). Both remain load-bearing
        // copywriter-RAG features; the separate Health nav entry was folded in.
        path: "social/copywriter/knowledge",
        label: "Knowledge Base",
        icon: Database,
        loader: () => import("./routes/social/Knowledge"),
      },
      {
        // Redirect shim → Knowledge hub Health tab. Preserves the old
        // /social/knowledge/health deep link + bookmarks.
        path: "social/knowledge/health",
        label: "Knowledge Health",
        icon: Activity,
        loader: redirectPreservingQuery("/social/copywriter/knowledge?tab=health"),
        hidden: true,
      },
    ],
  },
  {
    title: "Paid Media",
    routes: [
      {
        path: "social/paid-media/overview",
        label: "Overview",
        icon: Megaphone,
        loader: () => import("./routes/social/PaidMediaOverview"),
      },
      {
        path: "social/paid-media/accounts",
        label: "Campaigns",
        icon: Target,
        loader: () => import("./routes/social/PaidMediaAccounts"),
      },
      {
        // Alias route — same UI as /accounts, kept for external bookmarks.
        // Consolidated 2026-06-01 (was a separate Campaigns list page).
        path: "social/paid-media/campaigns",
        label: "Campaigns",
        icon: Target,
        loader: () => import("./routes/social/PaidMediaCampaigns"),
        hidden: true,
      },
      // Batch B (2026-05-27) — Phase 4-6: ads gallery, ad detail, leads,
      // lead forms, audit, audit log are all wired in. Detail routes are
      // declared with `hidden: true` so they resolve but don't crowd the
      // sidebar. Visible nav entries below.
      {
        path: "social/paid-media/ads",
        label: "Ads Gallery",
        icon: ImagePlay,
        loader: () => import("./routes/social/PaidMediaAdsGallery"),
      },
      {
        path: "social/paid-media/ads/:adId",
        label: "Ad Detail",
        icon: ImagePlay,
        loader: () => import("./routes/social/PaidMediaAdDetail"),
        hidden: true,
      },
      {
        path: "social/paid-media/leads",
        label: "Leads",
        icon: UserCheck,
        loader: () => import("./routes/social/PaidMediaLeads"),
      },
      {
        path: "social/paid-media/lead-forms",
        label: "Lead Forms",
        icon: FileText,
        loader: () => import("./routes/social/PaidMediaLeadForms"),
      },
      {
        path: "social/paid-media/lead-forms/:formId",
        label: "Lead Form Detail",
        icon: FileText,
        loader: () => import("./routes/social/PaidMediaLeadFormDetail"),
        hidden: true,
      },
      {
        path: "social/paid-media/campaigns/:campaignId",
        label: "Campaign Detail",
        icon: Target,
        loader: () => import("./routes/social/PaidMediaCampaignDetail"),
        hidden: true,
      },
      {
        path: "social/paid-media/audit",
        label: "Pre-Launch Audit",
        icon: ShieldCheck,
        loader: () => import("./routes/social/PaidMediaAudit"),
      },
      {
        path: "social/paid-media/audit-log",
        label: "Audit Log",
        icon: History,
        loader: () => import("./routes/social/PaidMediaAuditLog"),
      },
      {
        path: "social/paid-media/webhook-events",
        label: "Webhook Events",
        icon: Webhook,
        loader: () => import("./routes/social/PaidMediaWebhookEvents"),
      },
    ],
  },
  {
    title: "Settings",
    routes: [
      {
        // Accounts now lives as the "Accounts" tab inside the Overview hub.
        // Kept as a hidden redirect for deep links / internal navigate() calls.
        path: "social/accounts",
        label: "Accounts",
        icon: Users,
        loader: redirectPreservingQuery("/social/overview?tab=accounts"),
        hidden: true,
      },
      {
        path: "social/copywriter/settings",
        label: "Copywriter Config",
        icon: Sparkles,
        loader: () => import("./routes/social/CopywriterSettings"),
      },
      {
        path: "social/canva",
        label: "Canva Import",
        icon: Upload,
        loader: () => import("./routes/social/CanvaImport"),
      },
      {
        path: "social/archive",
        label: "Instagram Archive",
        icon: Archive,
        loader: () => import("./routes/social/Archive"),
      },
      {
        path: "social/templates",
        label: "Post Templates",
        icon: Copy,
        loader: () => import("./routes/social/Templates"),
      },
      {
        // Old per-app settings path — redirects to the unified /settings page
        // (auto-mounted by createShell via `config.settings`).
        // Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22.
        path: "social/settings",
        label: "Settings",
        icon: Settings,
        loader: () => import("@plerity/dashboard-shell/SettingsRedirect"),
        hidden: true,
      },
    ],
  },
];

declare const __CF_PAGES__: boolean;
const basename = typeof __CF_PAGES__ !== "undefined" && __CF_PAGES__ ? "/" : "/apps/social";

const Shell = createShell({
  name: "social",
  // Full name restored now that DashboardLayout/createShell support
  // `shortTitle` (backlog shell-short-title-phones-2026-10-04-001) — "Social"
  // was a temporary workaround (2026-10-04, live QA R8) for the shell header
  // having no short-title option at 375px, where header actions leave it
  // ~60px and "Social Media" truncated to "S…". `shortTitle` now renders
  // below `sm`; "Social Media" renders at `sm:` and up and remains the sole
  // accessible name at every viewport.
  displayName: "Social Media",
  shortTitle: "Social",
  sidebarGroups,
  defaultRoute: "/social/overview",
  headerActions: (
    <div className="flex items-center gap-1.5 sm:gap-2">
      {/* B6 Cmd-K palette — Navigate section derives from sidebarGroups above. */}
      <CommandPalette groups={sidebarGroups} />
      <ClientBrandSwitcher />
      <NotificationBell />
    </div>
  ),
  currentApp: "social",
  basename,
  // Data router — required by useShellNavigationBlocker (Design Studio
  // unsaved-edits prompt on in-app / back-button navigation).
  router: "data",
  settingsRoute: "/settings",
  // @allow-hardcoded — Hub SSO security settings URL; cannot be env-var in CF Pages static SPA build
  securityRoute: PORTAL_URLS.SECURITY,
  auth: {
    required: true,
    entitlement: "social",
  },
  chat: {
    enabled: true,
    features: [
      "compose",
      "calendar",
      "posts",
      "media-library",
      "templates",
      "hashtag-sets",
      "analytics",
      "accounts",
      "designs",
      "approvals",
    ],
    apiEndpoint: "/api/_assistant/chat",
    // Social's CommandPalette owns Ctrl/Cmd+K — unbind the assistant's
    // toggle (and its "Ctrl+K to toggle" hint) so the two never collide.
    toggleShortcut: false,
  },
  testerNotes: {
    enabled: true,
    contextId: "web-social",
  },
  // Unified Settings page — reached via top-right avatar → Settings.
  // Profile / Appearance / Notifications / Tester Notes / Assistant / Privacy
  // are auto-included by UserSettingsPage; the Social-specific tab is injected here.
  // Tester Notes tab is auto-hidden for non-super-admins (client-facing app).
  // Plan: web_shell_unified_settings_page_rollout_plan_2026-05-22.
  settings: {
    additionalTabs: [
      {
        id: "social-settings",
        label: "Social",
        icon: Settings,
        content: <SocialSettingsTabContent />,
      },
    ] as SettingsTab[],
    assistantName: "Social Assistant",
  },
});

function App() {
  // Handle OAuth callback routes before the Shell renders its BrowserRouter.
  // The basename may offset the pathname, so check both raw and basename-stripped paths.
  const rawPath = window.location.pathname;
  const strippedPath = basename !== "/" ? rawPath.replace(basename, "") || "/" : rawPath;
  if (strippedPath === "/auth/callback/facebook" || rawPath === "/auth/callback/facebook") {
    return <FacebookCallback />;
  }
  if (strippedPath === "/auth/callback/tiktok" || rawPath === "/auth/callback/tiktok") {
    return <TikTokCallback />;
  }
  if (strippedPath === "/auth/callback/linkedin" || rawPath === "/auth/callback/linkedin") {
    return <LinkedInCallback />;
  }
  if (strippedPath === "/auth/callback/google_business" || rawPath === "/auth/callback/google_business") {
    return <GoogleBusinessCallback />;
  }
  // Public client review (no login, outside the auth shell). The bearer token
  // lives in the URL fragment (#t=…) so it never reaches server/CDN logs.
  if (strippedPath === "/review" || rawPath === "/review") {
    return (
      <Suspense fallback={null}>
        <ClientReview />
      </Suspense>
    );
  }
  // Public client self-connect (no login). The OAuth callbacks above hand invite
  // states to the API, which redirects back here with ?status=… (lane D1).
  if (strippedPath === "/connect" || rawPath === "/connect") {
    return (
      <Suspense fallback={null}>
        <ClientConnect />
      </Suspense>
    );
  }
  // Public guest footage upload (no login). The link token lives in the URL
  // fragment (#t=…), never a query param, so it never reaches server/CDN logs.
  if (strippedPath === "/footage-upload" || rawPath === "/footage-upload") {
    return (
      <Suspense fallback={null}>
        <FootageUpload />
      </Suspense>
    );
  }
  if (strippedPath === "/auth/callback/pinterest" || rawPath === "/auth/callback/pinterest") {
    return <PinterestCallback />;
  }
  if (strippedPath === "/auth/callback/threads" || rawPath === "/auth/callback/threads") {
    return <ThreadsCallback />;
  }

  // Single app-root ConfirmProvider — powers the imperative useConfirm() API
  // that replaced window.confirm() across the app (delete/approve/GIF-limit
  // dialogs). Every route renders inside <Shell>, so the context is available
  // to all descendants.
  return (
    <ConfirmProvider>
      <Shell />
      {/* Learn-tip "?" overlay + toggle (scans data-help-context anchors).
          Rendered AFTER <Shell /> in DOM order so its fixed-position toggle
          isn't the first Tab stop on every page, ahead of the shell's own
          skip link (2026-10-04 QA finding) — its `fixed` positioning keeps
          it visually in the same bottom-right spot regardless of DOM order. */}
      <LearnTipsProvider />
    </ConfirmProvider>
  );
}

export default App;
